import { useMemo, useState } from "react";
import { SortingState } from "@/components/Datatable/types";

export type BillingSortValue = string | number | null;

export type BillingSortAccessors<T> = Partial<
  Record<keyof T & string, (row: T) => BillingSortValue>
>;

function compareSortValues(
  a: BillingSortValue,
  b: BillingSortValue,
  desc: boolean,
): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;

  const order =
    typeof a === "number" && typeof b === "number"
      ? a - b
      : String(a).localeCompare(String(b), undefined, { numeric: true });

  return desc ? -order : order;
}

export function useBillingClientSorting<T>(
  rows: T[],
  accessors: BillingSortAccessors<T>,
) {
  const [sorting, setSorting] = useState<SortingState>([]);

  const sortedRows = useMemo(() => {
    const sort = sorting[0];
    const getSortValue = sort && accessors[sort.id as keyof T & string];
    if (!getSortValue) return rows;

    return [...rows].sort((a, b) =>
      compareSortValues(getSortValue(a), getSortValue(b), sort.desc),
    );
  }, [rows, accessors, sorting]);

  return { sorting, setSorting, sortedRows };
}
